import React from "react";
import { Popover, Button, Typography, Box, Divider } from "@mui/material";
import { Link, CheckCheck, Check } from "lucide-react";

export type TriState = 'none' | 'include' | 'exclude';
export type InvoiceLinkFilterValue = { linked: TriState; fullySatisfied: TriState };

export interface InvoiceLinkFilterProps {
    anchorEl?: HTMLElement | null;
    open?: boolean;
    onClose?: () => void;
    value: InvoiceLinkFilterValue;
    onChange: (next: InvoiceLinkFilterValue) => void;
}

const OPTIONS: { key: keyof InvoiceLinkFilterValue; label: string; icon: React.ReactNode }[] = [
    { key: 'linked', label: 'Linked to a TX', icon: <Link size={15} color="var(--color-primary)" /> },
    { key: 'fullySatisfied', label: 'Fully Satisfied', icon: <CheckCheck size={15} color="var(--color-primary)" /> },
];

// Same three-way toggle as the Transactions page's Action column filter
// (actions-filter.tsx) - cycles neutral -> must-have -> must-not-have ->
// neutral per option, so "invoices not linked" is a single click (Linked ->
// exclude) instead of a separate include/exclude checkbox pair to scan.
const STATE_STYLE: Record<TriState, { bg: string; border: string; icon: React.ReactNode }> = {
    none: { bg: 'transparent', border: '#c7c7c7ff', icon: null },
    include: { bg: '#00b69b', border: '#00b69b', icon: <Check size={13} color="#fff" strokeWidth={3} /> },
    exclude: { bg: 'transparent', border: '#e05252', icon: <Box sx={{ width: 8, height: 8, borderRadius: '1.5px', background: '#e05252' }} /> },
};

const InvoiceLinkFilter: React.FC<InvoiceLinkFilterProps> = ({ anchorEl, open, onClose, value, onChange }) => {
    const cycle = (key: keyof InvoiceLinkFilterValue) => {
        const current = value[key];
        const next: TriState = current === 'none' ? 'include' : current === 'include' ? 'exclude' : 'none';
        onChange({ ...value, [key]: next });
    };

    return (
        <Popover
            open={!!open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{ sx: { borderRadius: 2, boxShadow: 3, p: 2, minWidth: 240 } }}
        >
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Filter</Typography>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" flexDirection="column" gap={0.5} mt={1}>
                {OPTIONS.map(option => {
                    const state = value[option.key];
                    const style = STATE_STYLE[state];
                    const label = state === 'exclude' ? `Not ${option.label}` : option.label;
                    return (
                        <Box
                            key={option.key}
                            onClick={() => cycle(option.key)}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{
                                cursor: 'pointer',
                                borderRadius: 1,
                                py: 0.5,
                                px: 0.5,
                                '&:hover': { background: 'var(--color-container-background)' },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 18,
                                    height: 18,
                                    flexShrink: 0,
                                    borderRadius: 0.75,
                                    border: `2.5px solid ${style.border}`,
                                    background: style.bg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {style.icon}
                            </Box>
                            {option.icon}
                            <Typography sx={{ fontSize: 13, fontWeight: state === 'exclude' ? 600 : 400 }}>{label}</Typography>
                        </Box>
                    );
                })}
            </Box>
            <Box display="flex" gap={1} mt={2}>
                <Button
                    fullWidth
                    variant="contained"
                    sx={{ background: 'var(--color-container-background)', color: 'var(--color-text-muted)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', '&:hover': { background: 'var(--color-container-background)' } }}
                    onClick={() => onChange({ linked: 'none', fullySatisfied: 'none' })}
                >
                    Clear
                </Button>
            </Box>
        </Popover>
    );
};

export default InvoiceLinkFilter;
